import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  computed,
  input,
  output,
  Output
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { AionzTooltip } from '@aionzdev/design-system';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
  transferArrayItem
} from '@angular/cdk/drag-drop';
import { PurchaseRequestCardData } from '../../../interfaces/pruchase-request-card-data';
import { PurchaseRequestCardComponent } from '../../atoms/purchase-request-card/purchase-request-card.component';
import { PurchaseRequestStatus } from '../../../enums';
import { KanbanChipBadgeComponent } from '../../atoms/kanban-chip-badge/kanban-chip-badge.component';
import { PurchaseRequestStatusComponent } from '../../atoms/purchase-request-status/purchase-request-status.component';
import { PurchaseStatusDirective } from '../../atoms/purchase-request-status/directives/purchase-request-status.directive';

@Component({
  selector: 'aionz-kanban-board',
  imports: [
    MatIcon,
    MatIconButton,
    MatChipsModule,
    AionzTooltip,
    PurchaseRequestCardComponent,
    DragDropModule,
    KanbanChipBadgeComponent,
    PurchaseRequestStatusComponent,
    PurchaseStatusDirective
  ],
  templateUrl: './kanban-board.component.html',
  styleUrl: './kanban-board.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class KanbanBoardComponent {
  public title = input.required<string>();
  public purchases = input.required<PurchaseRequestCardData[]>();
  public boardId = input.required<PurchaseRequestStatus>();
  public boardStatuses = input<string[]>([]);
  public isDraggable = input<boolean>(false);
  public showSlaBadge = input<boolean>(true);
  public averageSlaLabel = input<string>('4h');
  public showAddButton = input<boolean>(false);
  public showMenuButton = input<boolean>(false);
  public showTimeBadge = input<boolean>(false);
  public searchTerm = input('');
  /** Coluna recolhida: vira uma faixa estreita com título e contagem. */
  public collapsed = input<boolean>(false);
  public collapsedChange = output<boolean>();

  protected readonly collapsedTooltip = computed(() => {
    const count = this.filteredPurchases().length;
    const items = `${count} ${count === 1 ? 'solicitação' : 'solicitações'}`;
    const sla = this.showTimeBadge() && this.averageSlaLabel() !== '--' ? ` · média ${this.averageSlaLabel()}` : '';
    return `${this.title()}: ${items}${sla}. Clique para expandir.`;
  });

  protected setCollapsed(collapsed: boolean): void {
    this.collapsedChange.emit(collapsed);
  }

  public filteredPurchases = computed(() => {
    const items = this.purchases();
    const term = this.searchTerm().toLowerCase().trim();

    if (!term) {
      return items;
    }

    return items.filter(purchase => this.matchesSearchTerm(purchase, term));
  });

  @Output() cardDropped = new EventEmitter<{
    item: PurchaseRequestCardData;
    previousBoardId: PurchaseRequestStatus;
    currentBoardId: PurchaseRequestStatus;
    previousIndex: number;
    currentIndex: number;
  }>();
  @Output() cardClicked = new EventEmitter<PurchaseRequestCardData>();
  @Output() changeBuyer = new EventEmitter<PurchaseRequestCardData>();
  @Output() cancelPurchaseRequest = new EventEmitter<PurchaseRequestCardData>();
  @Output() openAuditReport = new EventEmitter<PurchaseRequestCardData>();

  private matchesSearchTerm(purchase: PurchaseRequestCardData, term: string): boolean {
    if (purchase.title.toLowerCase().includes(term)) {
      return true;
    }

    if (purchase.description.toLowerCase().includes(term)) {
      return true;
    }

    if (purchase.requesters.some(requester => requester.name.toLowerCase().includes(term))) {
      return true;
    }

    if (purchase.buyer?.responsibleName?.toLowerCase().includes(term)) {
      return true;
    }

    if (purchase.id.toLowerCase().includes(term)) {
      return true;
    }

    return false;
  }

  onDrop(event: CdkDragDrop<PurchaseRequestCardData[]>) {
    if (!this.isDraggable()) return;

    const draggedItem = event.item.data;

    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }

    this.cardDropped.emit({
      item: draggedItem,
      previousBoardId: event.previousContainer.id as PurchaseRequestStatus,
      currentBoardId: event.container.id as PurchaseRequestStatus,
      previousIndex: event.previousIndex,
      currentIndex: event.currentIndex
    });
  }
}
